import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, HandHeart, Users, GraduationCap, Landmark, Microscope, BookOpen } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ways = [
  { icon: Users, text: "Connecting you with expert speakers and subject matter experts" },
  { icon: GraduationCap, text: "Developing educational programs, trainings, and resources" },
  { icon: Landmark, text: "Supporting advocacy, policy, and public awareness initiatives" },
  { icon: Microscope, text: "Collaborating on research, quality improvement, and community projects" },
  { icon: BookOpen, text: "Sharing evidence-based best practices in addiction prevention, treatment, recovery, and overdose prevention" },
];

export default function SolicitSupport() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", organization: "", phone: "",
    request_type: "speaker", description: "", timeline: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/support-requests", form);
      setSent(true);
      toast.success("Thanks — a member of our team will be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="support-request-success">
        <CheckCircle2 size={48} className="mx-auto text-[color:var(--azsam-sage-hover)]" strokeWidth={1.5} />
        <div className="overline mt-6">Received</div>
        <h1 className="mt-4 font-heading text-4xl">Thank you.</h1>
        <p className="mt-4 text-[color:var(--azsam-ink-soft)]">
          Your request has been received. A member of the AzSAM team will be in touch soon to
          explore how we can help support your goals.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/" className="btn-outline" data-testid="support-back-home">← Back to home</Link>
          <button onClick={() => { setSent(false); setForm({ name:"",email:"",organization:"",phone:"",request_type:"speaker",description:"",timeline:"" }); }} className="btn-primary" data-testid="support-submit-another">Submit another</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16" data-testid="solicit-support-page">
      <div className="max-w-3xl">
        <div className="overline">Partner with AzSAM</div>
        <h1 className="mt-5 font-heading text-4xl sm:text-5xl leading-tight">How Can We Help You?</h1>
        <p className="mt-6 text-lg text-[color:var(--azsam-ink-soft)] leading-relaxed">
          At AzSAM, we believe meaningful change happens through collaboration. Whether you're
          planning an educational event, launching a community initiative, addressing a public
          policy issue, seeking addiction medicine expertise, or looking for ways to better support
          individuals and families affected by substance use disorders, we would love the
          opportunity to work with you. Our members include physicians, nurses, behavioral health
          professionals, researchers, educators, and community leaders dedicated to advancing
          evidence-based addiction prevention, treatment, recovery, and public awareness throughout
          Arizona.
        </p>
      </div>

      <div className="mt-12 azsam-card p-8 lg:p-10">
        <div className="flex items-center gap-3">
          <HandHeart size={22} className="text-[color:var(--azsam-clay)]" strokeWidth={1.6} />
          <div className="overline">AzSAM may be able to assist with</div>
        </div>
        <ul className="mt-6 space-y-4">
          {ways.map((w, i) => (
            <li key={i} className="flex gap-4 items-start" data-testid={`support-way-${i}`}>
              <div className="w-9 h-9 rounded-lg bg-[color:var(--azsam-sage)]/15 text-[color:var(--azsam-sage-hover)] flex items-center justify-center flex-shrink-0">
                <w.icon size={16} strokeWidth={1.7} />
              </div>
              <div className="text-[color:var(--azsam-ink)] leading-relaxed pt-1.5">{w.text}</div>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-[color:var(--azsam-ink-soft)] leading-relaxed">
          Have an idea or opportunity not listed above? We'd love to hear about it. Complete the
          information below and a member of our team will be in touch soon to explore how AzSAM can
          help support your goals and strengthen addiction-related efforts across Arizona.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-10 azsam-card p-8 lg:p-10 space-y-5" data-testid="support-request-form">
        <div className="overline">Your request</div>
        <div className="grid md:grid-cols-2 gap-5">
          <F label="Your full name" required>
            <input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className={input} data-testid="support-name" placeholder="Dr. Casey Rivera" />
          </F>
          <F label="Email" required>
            <input required type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className={input} data-testid="support-email" placeholder="you@organization.org" />
          </F>
          <F label="Organization">
            <input value={form.organization} onChange={(e)=>setForm({...form,organization:e.target.value})} className={input} data-testid="support-org" />
          </F>
          <F label="Phone (optional)">
            <input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className={input} data-testid="support-phone" />
          </F>
        </div>

        <F label="Type of request" required>
          <Select value={form.request_type} onValueChange={(v) => setForm({...form, request_type: v})}>
            <SelectTrigger className="h-12 border-[color:var(--azsam-stone)] rounded-lg" data-testid="support-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="speaker">Speaker / subject-matter expert</SelectItem>
              <SelectItem value="education">Educational program or training</SelectItem>
              <SelectItem value="advocacy">Advocacy, policy, or public awareness</SelectItem>
              <SelectItem value="research">Research, quality improvement, or community project</SelectItem>
              <SelectItem value="best_practices">Best practices in prevention / treatment / recovery / overdose</SelectItem>
              <SelectItem value="other">Something else</SelectItem>
            </SelectContent>
          </Select>
        </F>

        <F label="Tell us about your project or request" required>
          <textarea required rows={6} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className={input + " resize-y"} data-testid="support-description" placeholder="What are you working on, and how might AzSAM help?" />
        </F>

        <F label="Timeline (optional)">
          <input value={form.timeline} onChange={(e)=>setForm({...form,timeline:e.target.value})} className={input} data-testid="support-timeline" placeholder="e.g., needed by March 2027, ongoing, flexible…" />
        </F>

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="support-submit">
            {submitting ? "Sending…" : (<>Send request <ArrowRight size={16} /></>)}
          </button>
        </div>
      </form>
    </div>
  );
}

const input = "w-full px-4 py-3 rounded-lg border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)] transition";

function F({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[color:var(--azsam-ink-soft)] mb-2 font-semibold">
        {label}{required && <span className="text-[color:var(--azsam-clay)]"> *</span>}
      </div>
      {children}
    </label>
  );
}
